import { useState } from 'react';
import { Loader2, AlertTriangle, Calendar, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import {
    usePagosBySuscripcionQuery,
    useUpdatePagoMutation,
    useDeletePagoMutation,
} from '../../services/hooks/usePagos';
import { Suscripcion } from '../../types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useQueryClient } from '@tanstack/react-query';
import ConfirmationModal from '../common/ConfirmationModal';

interface PagosClienteSectionProps {
    clientId: string;
    suscripcionOtrosPagos: Suscripcion | undefined;
    clienteActivo: boolean;
}

const PagosClienteSection: React.FC<PagosClienteSectionProps> = ({ 
    clientId, 
    suscripcionOtrosPagos,
    clienteActivo 
}) => {
    const queryClient = useQueryClient();

    // Cargar pagos solo si tiene suscripción de otros pagos
    const { data: pagos } = usePagosBySuscripcionQuery(suscripcionOtrosPagos?._id || "");

    // Mutaciones: pasan por apiClient, que añade el JWT de Clerk y el prefijo de tenant
    const updatePagoMutation = useUpdatePagoMutation();
    const deletePagoMutation = useDeletePagoMutation();

    const closingPago = updatePagoMutation.isPending;
    const deletingPago = deletePagoMutation.isPending;

    // Estado para mostrar/ocultar la sección de pagos
    const [showPagos, setShowPagos] = useState(true);

    // Estados para cerrar pagos
    const [showClosePagoModal, setShowClosePagoModal] = useState(false);
    const [selectedPagoId, setSelectedPagoId] = useState<string | null>(null);
    const [selectedFechaPago, setSelectedFechaPago] = useState<Date>(new Date());

    // Estados para eliminar pagos
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pagoToDelete, setPagoToDelete] = useState<string | null>(null);

    // Función para formatear números
    const formatNumber = (num: number | null | undefined): string => {
        const safeNum = num ?? 0;
        const parts = safeNum.toFixed(2).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return parts.join(',');
    };

    // Función para cerrar un pago abierto
    const handleCerrarPago = async () => {
        if (!selectedPagoId || closingPago) return;

        // El endpoint espera el pago completo; partimos del actual y sobreescribimos
        const pago = pagos?.find((p) => p._id === selectedPagoId);
        if (!pago) return;

        try {
            await updatePagoMutation.mutateAsync({
                ...pago,
                estado: 'Cerrado' as const,
                fechaPago: selectedFechaPago.toISOString(),
            });

            // La mutación ya invalida ['pagos']; el detalle del cliente no
            queryClient.invalidateQueries({ queryKey: ['client', clientId] });

            // Cerrar modal y resetear estados
            setShowClosePagoModal(false);
            setSelectedPagoId(null);
            setSelectedFechaPago(new Date());
        } catch (error) {
            // La mutación ya muestra el toast de error
            console.error('Error al cerrar pago:', error);
        }
    };

    // Función para eliminar un pago
    const handleDeletePago = async () => {
        if (!pagoToDelete || deletingPago) return;

        try {
            await deletePagoMutation.mutateAsync(pagoToDelete);

            // La mutación ya invalida ['pagos']; el detalle del cliente no
            queryClient.invalidateQueries({ queryKey: ['client', clientId] });

            // Cerrar modal y resetear estados
            setShowDeleteModal(false);
            setPagoToDelete(null);
        } catch (error) {
            // La mutación ya muestra el toast de error
            console.error('Error al eliminar pago:', error);
        }
    };

    if (!suscripcionOtrosPagos) {
        return null;
    }

    return (
        <>
            <div className="mt-6 bg-white p-6 rounded-lg shadow">
                <button 
                    onClick={() => setShowPagos(!showPagos)}
                    className="w-full flex justify-between items-center border-b pb-2 mb-4"
                >
                    <div className="flex items-center">
                        <svg className="w-5 h-5 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"/>
                        </svg>
                        <h2 className="text-lg font-bold">Historial de Pagos</h2>
                    </div>
                    {showPagos ? <ChevronUp /> : <ChevronDown />}
                </button>
                
                {showPagos && (
                    <>
                        <p className="text-sm text-gray-600 mb-4">
                            Gestión de pagos mensuales de la suscripción de otros pagos
                        </p>
                        
                        {pagos && pagos.length > 0 ? (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {pagos
                            .sort((a, b) => {
                                // Ordenar por año y mes (más reciente primero)
                                if (a.anio !== b.anio) return b.anio - a.anio;
                                return b.mes - a.mes;
                            })
                            .map((pago) => {
                                const meses = [
                                    '', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                                    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
                                ];
                                const nombreMes = meses[pago.mes] || `Mes ${pago.mes}`;
                                const esPagoAbierto = pago.estado === 'Abierto';
                                
                                return (
                                    <div key={pago._id} className={`border rounded-lg p-4 relative ${
                                        esPagoAbierto 
                                            ? 'border-yellow-300 bg-yellow-50' 
                                            : 'border-green-300 bg-green-50'
                                    }`}>
                                        {/* Botón de eliminar */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (pago._id) {
                                                    setPagoToDelete(pago._id);
                                                    setShowDeleteModal(true);
                                                }
                                            }}
                                            className="absolute top-2 right-2 p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                                            title="Eliminar pago"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                        
                                        <div className="flex justify-between items-start mb-2">
                                            <h3 className="font-semibold text-gray-900">
                                                {nombreMes} {pago.anio}
                                            </h3>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                esPagoAbierto
                                                    ? 'bg-yellow-200 text-yellow-800'
                                                    : 'bg-green-200 text-green-800'
                                            }`}>
                                                {pago.estado}
                                            </span>
                                        </div>
                                        
                                        <div className="space-y-1 text-sm">
                                            <p><strong>Importe:</strong> {formatNumber(pago.importe)}€</p>
                                            
                                            {pago.fechaPago && (
                                                <p><strong>Fecha de pago:</strong> {new Date(pago.fechaPago).toLocaleDateString()}</p>
                                            )}
                                            
                                            {pago.metodoPago && (
                                                <p><strong>Método:</strong> {pago.metodoPago}</p>
                                            )}
                                            
                                            {pago.observaciones && pago.observaciones.trim() !== '' && (
                                                <p><strong>Observaciones:</strong> {pago.observaciones}</p>
                                            )}
                                            
                                            {esPagoAbierto && (
                                                <>
                                                    <div className="mt-2 p-2 bg-yellow-100 rounded text-xs text-yellow-800">
                                                        <span className="flex items-center">
                                                            <AlertTriangle size={12} className="mr-1" />
                                                            Pago pendiente de cobro
                                                        </span>
                                                    </div>
                                                    {clienteActivo && (
                                                        <button
                                                            onClick={() => {
                                                                if (pago._id) {
                                                                    setSelectedPagoId(pago._id);
                                                                    setSelectedFechaPago(new Date());
                                                                    setShowClosePagoModal(true);
                                                                }
                                                            }}
                                                            className="mt-2 w-full px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium transition-colors"
                                                        >
                                                            Cerrar Pago
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        }
                        </div>
                        
                        {/* Resumen de pagos */}
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                                <div>
                                    <p className="text-2xl font-bold text-blue-600">{pagos.length}</p>
                                    <p className="text-sm text-gray-600">Total pagos</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-green-600">
                                        {pagos.filter(p => p.estado === 'Cerrado').length}
                                    </p>
                                    <p className="text-sm text-gray-600">Pagados</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-yellow-600">
                                        {pagos.filter(p => p.estado === 'Abierto').length}
                                    </p>
                                    <p className="text-sm text-gray-600">Pendientes</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-gray-800">
                                        {formatNumber(pagos.reduce((total, pago) => total + pago.importe, 0))}€
                                    </p>
                                    <p className="text-sm text-gray-600">Importe total</p>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    /* Estado vacío - sin pagos */
                    <div className="text-center py-12">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                            <svg className="h-6 w-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"/>
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay pagos registrados</h3>
                        <p className="text-gray-500 mb-4">
                            Los pagos para esta suscripción aparecerán aquí una vez que se generen.
                        </p>
                        <p className="text-sm text-gray-400">
                            Los pagos mensuales se crearán automáticamente según la configuración de la suscripción.
                        </p>
                    </div>
                )}
                    </>
                )}
            </div>

            {/* Modal para cerrar pago */}
            {showClosePagoModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
                        <div className="flex items-center text-green-600 mb-4">
                            <Calendar size={24} className="mr-2" />
                            <h3 className="text-xl font-bold">Cerrar Pago</h3>
                        </div>
                        
                        <p className="mb-4 text-gray-700">
                            Está a punto de marcar este pago como cerrado. Seleccione la fecha de cobro:
                        </p>
                        
                        <div className="mb-4">
                            <label className="block mb-2 text-sm font-medium text-gray-700">
                                Fecha de cobro:
                            </label>
                            <DatePicker
                                selected={selectedFechaPago}
                                onChange={(date: Date | null) => {
                                    if (date) {
                                        setSelectedFechaPago(date);
                                    }
                                }}
                                dateFormat="dd/MM/yyyy"
                                className="border border-gray-300 p-2 rounded w-full focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                placeholderText="Seleccione una fecha"
                                maxDate={new Date()}
                            />
                        </div>
                        
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowClosePagoModal(false);
                                    setSelectedPagoId(null);
                                    setSelectedFechaPago(new Date());
                                }}
                                disabled={closingPago}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleCerrarPago}
                                disabled={closingPago}
                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
                            >
                                {closingPago ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin mr-2" />
                                        Procesando...
                                    </>
                                ) : (
                                    'Confirmar y Cerrar'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmación para eliminar pago */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setPagoToDelete(null);
                }}
                onConfirm={handleDeletePago}
                confirmMessage="¿Estás seguro de que quieres <strong>ELIMINAR ESTE PAGO</strong>? Esta acción no se puede deshacer."
                isLoading={deletingPago}
            />
        </>
    );
};

export default PagosClienteSection;
